import { isValidCanonicalKey } from "../data/canonical";
import { share } from "../data/rating";
import {
  computeStanding,
  eventsSinceAnchor,
  indexInstances,
  standingKey,
} from "../data/standing";
import type { Dimension, Instance, Person, Tag } from "../data/model";
import { pct } from "../ui/dom";
import type { CompleteOptions } from "./provider";

// The register every function writes in: house style and formatting. Shared so
// the generative propose-from-role prompt keeps the same voice as the data
// partner without inheriting its over-its-own-data-only clause.
const STYLE_RULES =
  "British English. No em dashes. Write plain prose, with short dashes for any " +
  "lists; do NOT use markdown, asterisks or bold.";

// Shared guardrail: the model is a partner over the USER'S OWN data only. It may
// never introduce a person or a fact that is not in the supplied corpus.
const GROUND_RULES =
  "You work only from the DATA block below, which is the user's own private " +
  "judgements. Never invent a person, a tag, or a fact that is not present. " +
  "Do not bring in outside knowledge about any named person. If the data is " +
  "thin or absent, say so plainly rather than filling the gap. " +
  STYLE_RULES;

// One rated person, serialised, plus a lowercased haystack for relevance
// scoring. Untagged people never become records: they carry no signal and only
// dilute the prompt (and would balloon it after a large contacts import).
export interface PersonRecord {
  id: string;
  name: string;
  text: string;
  haystack: string;
}

export function buildRecords(
  people: Person[],
  dimensions: Dimension[],
  tags: Tag[],
  instances: Instance[] = [],
  now = 0,
): PersonRecord[] {
  const dimName = new Map(dimensions.map((d) => [d.id, d.name]));
  const instIdx = indexInstances(instances);
  const byPerson = new Map<string, Tag[]>();
  const standingByTag = new Map<string, ReturnType<typeof computeStanding>>();
  const dimTotals = new Map<string, number>();
  for (const t of tags) {
    const list = byPerson.get(t.person_id) ?? [];
    list.push(t);
    byPerson.set(t.person_id, list);
    const allEvents = instIdx.get(standingKey(t.person_id, t.dimension_id)) ?? [];
    const since = eventsSinceAnchor(allEvents, t.read_at ?? t.created_at);
    const st = computeStanding(t.rating, since, now);
    standingByTag.set(t.id, st);
    dimTotals.set(t.dimension_id, (dimTotals.get(t.dimension_id) ?? 0) + st.point);
  }

  const records: PersonRecord[] = [];
  for (const p of people) {
    const mine = (byPerson.get(p.id) ?? []).slice();
    if (mine.length === 0) continue; // exclude untagged
    mine.sort((a, b) => standingByTag.get(b.id)!.point - standingByTag.get(a.id)!.point);
    const ctx = p.relationship_context ? ` — context: ${p.relationship_context}` : "";
    const lines = [`- ${p.display_name}${ctx}`];
    const evidence: string[] = [];
    for (const t of mine) {
      const st = standingByTag.get(t.id)!;
      if (t.rationale) evidence.push(t.rationale);
      const why = t.rationale ? ` — "${t.rationale}"` : "";
      const s = share(st.point, dimTotals.get(t.dimension_id) ?? 0);
      // Where events exist, cite them as evidence quality metadata: the read,
      // the evidence-updated standing, and the indicative band.
      let annot = "";
      if (st.eventCount > 0 && st.band) {
        annot =
          ` [read ${st.read}, ${st.eventCount} events, standing ${st.point.toFixed(1)}; ` +
          `indicative reliability ${st.band.low.toFixed(1)}-${st.band.high.toFixed(1)}]`;
      }
      lines.push(`    ${dimName.get(t.dimension_id) ?? "?"}: ${pct(s)} share${why}${annot}`);
    }
    records.push({
      id: p.id,
      name: p.display_name,
      text: lines.join("\n"),
      haystack: `${p.display_name} ${p.relationship_context} ${evidence.join(" ")}`.toLowerCase(),
    });
  }
  return records;
}

export function recordsToText(records: PersonRecord[]): string {
  if (records.length === 0) return "DATA: (no rated people yet)";
  return ["DATA — the user's rated people and their own ratings:", ...records.map((r) => r.text)].join("\n");
}

// A factual summary the model can use to spot rating inflation (ratings
// clustered high, so top reads are not differentiated) and uneven coverage
// (people rated on only one quality). Given to SURFACE alongside the corpus.
export function buildStats(
  people: Person[],
  dimensions: Dimension[],
  tags: Tag[],
): string {
  if (tags.length === 0) return "";
  const byDim = new Map<string, number[]>();
  const cover = new Map<string, Set<string>>();
  for (const t of tags) {
    const rs = byDim.get(t.dimension_id) ?? [];
    rs.push(t.rating);
    byDim.set(t.dimension_id, rs);
    const seen = cover.get(t.person_id) ?? new Set<string>();
    seen.add(t.dimension_id);
    cover.set(t.person_id, seen);
  }

  const lines = ["RATING DISTRIBUTION (people rated at each level 1..5, per quality):"];
  for (const d of dimensions) {
    const rs = byDim.get(d.id);
    if (!rs || rs.length === 0) continue;
    const counts = [1, 2, 3, 4, 5].map((l) => rs.filter((r) => r === l).length);
    const mean = (rs.reduce((a, b) => a + b, 0) / rs.length).toFixed(1);
    lines.push(
      `${d.name}: ${counts.map((c, i) => `${c}x${i + 1}`).join(", ")} (mean ${mean} over ${rs.length})`,
    );
  }

  const ratedCount = cover.size;
  const avg = (
    [...cover.values()].reduce((a, s) => a + s.size, 0) / Math.max(1, ratedCount)
  ).toFixed(1);
  lines.push(
    `COVERAGE: ${ratedCount} people rated; on average each on ${avg} of ${dimensions.length} qualities.`,
  );
  const thin = people
    .filter((p) => (cover.get(p.id)?.size ?? 0) === 1)
    .map((p) => p.display_name);
  if (thin.length > 0) {
    lines.push(
      `Rated on only one quality: ${thin.slice(0, 12).join(", ")}${thin.length > 12 ? ", ..." : ""}.`,
    );
  }
  return lines.join("\n");
}

// Back-compatible: the full rated-people corpus (tagged only).
export function buildCorpus(
  people: Person[],
  dimensions: Dimension[],
  tags: Tag[],
  instances: Instance[] = [],
  now = 0,
): string {
  return recordsToText(buildRecords(people, dimensions, tags, instances, now));
}

export const ASSEMBLE_PERSON_LIMIT = 40;

const STOP = new Set([
  "the", "and", "for", "who", "what", "with", "you", "your", "our", "are",
  "that", "this", "would", "should", "could", "put", "make", "whom", "they",
  "them", "into", "over", "from", "have", "has", "not", "but", "about", "when",
  "where", "which", "will", "want", "need",
]);

function tokenize(text: string): string[] {
  return [
    ...new Set(
      text
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter((w) => w.length > 2 && !STOP.has(w)),
    ),
  ];
}

// Bound the ASSEMBLE prompt at scale: when there are more rated people than the
// limit, keep those most relevant to the decision text (lexical overlap), so a
// large book of contacts does not blow the context window or dilute the brief.
export function selectRelevant(
  records: PersonRecord[],
  decision: string,
  limit: number,
): { selected: PersonRecord[]; truncated: boolean } {
  if (records.length <= limit) return { selected: records, truncated: false };
  const terms = tokenize(decision);
  const scored = records.map((r, i) => ({
    r,
    i,
    score: terms.reduce((n, t) => n + (r.haystack.includes(t) ? 1 : 0), 0),
  }));
  scored.sort((a, b) => b.score - a.score || a.i - b.i);
  return { selected: scored.slice(0, limit).map((s) => s.r), truncated: true };
}

// SHARPEN: exactly one calibration question to make a single read sharper.
export function sharpenPrompt(
  personName: string,
  dimensionName: string,
  rationale: string,
): CompleteOptions {
  const current = rationale.trim()
    ? `Their current note reads: "${rationale.trim()}".`
    : "There is no note yet.";
  return {
    system:
      "You help an allocator calibrate a judgement about one person on one " +
      "quality. Ask exactly ONE short, probing question that would make the " +
      "read more precise (for example: compared with whom? what decision " +
      "earned it? when did it last hold?). Output only the question, one line, " +
      "no preamble. British English. No em dashes.",
    messages: [
      {
        role: "user",
        content: `Person: ${personName}\nQuality: ${dimensionName}\n${current}`,
      },
    ],
    maxTokens: 120,
    temperature: 0.5,
  };
}

// SURFACE: patterns and blind spots across the whole rated set.
export function surfacePrompt(corpus: string): CompleteOptions {
  return {
    system:
      GROUND_RULES +
      " Analyse the whole set and report PATTERNS and BLIND SPOTS the user may " +
      "not see. Use the RATING DISTRIBUTION and COVERAGE figures explicitly: " +
      "call out RATING INFLATION where ratings cluster high (mostly 4s and 5s) " +
      "so the user's top reads are not differentiated, naming the quality; flag " +
      "UNEVEN COVERAGE where people are rated on only one quality (thin reads). " +
      "Also cover concentration by network or sector (infer only from the stated " +
      "relationship contexts) and skew towards particular qualities. Be specific " +
      "and cite people or qualities. Short headed sections, under 250 words.",
    messages: [{ role: "user", content: corpus }],
    maxTokens: 900,
    temperature: 0.3,
  };
}

// A proposed quality from the role flow: a name, a one-line definition, and a
// proposed backbone mapping (a canonical key or null). Never committed until the
// user edits and confirms (see settings / partner).
export interface ProposedDimension {
  name: string;
  definition: string;
  canonical_key: string | null;
}

// Normalise a model-supplied key: a real backbone key passes, anything else
// (including "none", "", a hallucinated key) becomes null. The backbone is the
// only allowed vocabulary; the model may not invent a quality.
function normaliseCanonicalKey(raw: string): string | null {
  const key = raw.trim().toLowerCase();
  return isValidCanonicalKey(key) && key !== "" ? key : null;
}

// PROPOSE: suggest 6-8 non-overlapping qualities for a stated role, in the
// packs' register, each pre-mapped to a backbone key or none. Generative, so it
// does NOT carry the over-own-data guardrail; it reuses the house style and is
// seeded with the packs as exemplars and the backbone as the only key options.
export function proposeDimensionsPrompt(
  role: string,
  exemplars: { label: string; rows: { name: string; definition: string; canonical_key: string | null }[] }[],
  canonical: { key: string; name: string }[],
): CompleteOptions {
  const examples = exemplars
    .map(
      (e) =>
        `${e.label}:\n` +
        e.rows
          .map((r) => `${r.name} | ${r.canonical_key ?? "none"} | ${r.definition}`)
          .join("\n"),
    )
    .join("\n\n");
  const keyList = canonical.map((c) => `${c.key} (${c.name})`).join(", ");
  return {
    system:
      "You help someone set up a private tool for judging people in a stated " +
      "role. Propose between 6 and 8 qualities that matter for that role, each " +
      "distinct and non-overlapping, each with a one-line definition. For each, " +
      "map it to the SINGLE nearest shared quality from this fixed list, or to " +
      "'none' if none fits. Do not invent a key outside the list.\n\n" +
      `Shared qualities: ${keyList}.\n\n` +
      "Match the register of these examples:\n\n" +
      examples +
      "\n\nOutput ONLY the qualities, one per line, as 'Name | key-or-none | " +
      "definition'. No numbering, no preamble, no closing line. " +
      STYLE_RULES,
    messages: [{ role: "user", content: `Role: ${role.trim()}` }],
    maxTokens: 600,
    temperature: 0.4,
  };
}

// Parse the model's 'Name | key | definition' lines into proposed dimensions.
// Tolerant of blank lines and stray numbering; an unknown key becomes null. Pure,
// so the parse-and-confirm flow is testable without a model.
export function parseProposedDimensions(text: string): ProposedDimension[] {
  const out: ProposedDimension[] = [];
  for (const raw of text.split("\n")) {
    const line = raw.trim().replace(/^[\s\-*\d.)]+/, "").trim();
    if (!line) continue;
    const parts = line.split("|").map((s) => s.trim());
    if (parts.length < 3) continue;
    const [name, key, ...rest] = parts;
    const definition = rest.join(" | ").trim();
    if (!name || !definition) continue;
    out.push({ name, definition, canonical_key: normaliseCanonicalKey(key ?? "") });
  }
  return out;
}

// A single canonical suggestion for one custom dimension: the nearest backbone
// key (or null) with a one-line reason. Preselected for the user, never committed.
export interface CanonicalSuggestion {
  canonical_key: string | null;
  reason: string;
}

// SUGGEST: pick the single nearest backbone key for a custom quality, or none.
// Reuses GROUND_RULES (the backbone is the only allowed vocabulary; no invention).
export function suggestCanonicalPrompt(
  name: string,
  definition: string,
  canonical: { key: string; name: string; definition: string }[],
): CompleteOptions {
  const list = canonical
    .map((c) => `${c.key} (${c.name}): ${c.definition}`)
    .join("\n");
  return {
    system:
      GROUND_RULES +
      " The DATA is a fixed list of shared qualities. Choose the SINGLE key " +
      "whose meaning is nearest to the user's quality, or 'none' if none is a " +
      "good fit. Never invent a key. Output one line only as 'key-or-none: a " +
      "short reason'.",
    messages: [
      {
        role: "user",
        content: `DATA — shared qualities:\n${list}\n\nUser quality: ${name} - ${definition.trim()}`,
      },
    ],
    maxTokens: 120,
    temperature: 0.2,
  };
}

// Parse 'key: reason' into a validated suggestion; an unknown key becomes null.
export function parseCanonicalSuggestion(text: string): CanonicalSuggestion {
  const line = text.trim().split("\n")[0] ?? "";
  const i = line.indexOf(":");
  const rawKey = i >= 0 ? line.slice(0, i) : line;
  const reason = i >= 0 ? line.slice(i + 1).trim() : "";
  return { canonical_key: normaliseCanonicalKey(rawKey), reason };
}

// ASSEMBLE: a structured reasoning brief for a decision, from stored data only.
export function assemblePrompt(
  corpus: string,
  decision: string,
  subset?: { shown: number; total: number },
): CompleteOptions {
  const subsetNote = subset
    ? ` The DATA shows the ${subset.shown} of ${subset.total} rated people most ` +
      "relevant to this decision; say so if coverage feels partial."
    : "";
  return {
    system:
      GROUND_RULES +
      subsetNote +
      " Given the user's decision, produce a short reasoning brief with three " +
      "headed parts: WHOM YOU RATE (the relevant people from the data, each " +
      "with the quality and the user's own read), TENSIONS (where their reads " +
      "conflict or a strength offsets a weakness), and GAPS (qualities or " +
      "coverage the data lacks for this decision). Where a rating shows logged " +
      "instances, treat them as evidence: note the indicative reliability band " +
      "and any unresolved divergence (e.g. rated high but thinly or " +
      "inconsistently evidenced). Never call the band a confidence interval. " +
      "Reference only people in the data. Keep it under 300 words.",
    messages: [
      { role: "user", content: `${corpus}\n\nDECISION: ${decision.trim()}` },
    ],
    maxTokens: 1100,
    temperature: 0.3,
  };
}
